import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';

dayjs.extend(duration);

export const isIterableArray = (array: any[]): boolean => Array.isArray(array) && !!array.length;

//===============================
// Breakpoints
//===============================
export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1540
};

export const getItemFromStore = (key: string, defaultValue: any, store = localStorage) => {
  try {
    return store.getItem(key) === null
      ? defaultValue
      : JSON.parse(store.getItem(key) as string);
  } catch {
    return store.getItem(key) || defaultValue;
  }
};

export const setItemToStore = (key: string, payload: any, store = localStorage) =>
  store.setItem(key, payload);

export const getStoreSpace = (store = localStorage) =>
  parseFloat(
    (
      escape(encodeURIComponent(JSON.stringify(store))).length /
      (1024 * 1024)
    ).toFixed(2)
  );

//===============================
// Cookie
//===============================
export const getCookieValue = (name: string) => {
  const value = document.cookie.match(
    '(^|[^;]+)\\s*' + name + '\\s*=\\s*([^;]+)'
  );
  return value ? value.pop() : null;
};

export const createCookie = (name: string, value: string, cookieExpireTime: number) => {
  const date = new Date();
  date.setTime(date.getTime() + cookieExpireTime);
  const expires = '; expires=' + date.toUTCString();
  document.cookie = name + '=' + value + expires + '; path=/';
};

export const numberFormatter = (number: number, fixed = 2) => {
  // Nine Zeroes for Billions
  return Math.abs(Number(number)) >= 1.0e9
    ? (Math.abs(Number(number)) / 1.0e9).toFixed(fixed) + 'B'
    : // Six Zeroes for Millions
    Math.abs(Number(number)) >= 1.0e6
    ? (Math.abs(Number(number)) / 1.0e6).toFixed(fixed) + 'M'
    : // Three Zeroes for Thousands
    Math.abs(Number(number)) >= 1.0e3
    ? (Math.abs(Number(number)) / 1.0e3).toFixed(fixed) + 'K'
    : Math.abs(Number(number)).toFixed(fixed);
};

//===============================
// Colors
//===============================
export const hexToRgb = (hexValue: string) => {
  let hex;
  hexValue.indexOf('#') === 0
    ? (hex = hexValue.substring(1))
    : (hex = hexValue);
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    hex.replace(shorthandRegex, (m: string, r: string, g: string, b: string) => r + r + g + g + b + b)
  );
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
      ]
    : null;
};

export const rgbColor = (color = colors[0]) => `rgb(${hexToRgb(color)})`;
export const rgbaColor = (color = colors[0], alpha = 0.5) =>
  `rgba(${hexToRgb(color)},${alpha})`;

export const colors = [
  '#2c7be5',
  '#00d97e',
  '#e63757',
  '#39afd1',
  '#fd7e14',
  '#02a8b5',
  '#727cf5',
  '#6b5eae',
  '#ff679b',
  '#f6c343'
];

export const themeColors = {
  primary: '#2c7be5',
  secondary: '#748194',
  success: '#00d27a',
  info: '#27bcfd',
  warning: '#f5803e',
  danger: '#e63757',
  light: '#f9fafd',
  dark: '#0b1727'
};

export const grays = {
  white: '#fff',
  100: '#f9fafd',
  200: '#edf2f9',
  300: '#d8e2ef',
  400: '#b6c1d2',
  500: '#9da9bb',
  600: '#748194',
  700: '#5e6e82',
  800: '#4d5969',
  900: '#344050',
  1000: '#232e3c',
  1100: '#0b1727',
  black: '#000'
};

export const darkGrays = {
  white: '#fff',
  1100: '#f9fafd',
  1000: '#edf2f9',
  900: '#d8e2ef',
  800: '#b6c1d2',
  700: '#9da9bb',
  600: '#748194',
  500: '#5e6e82',
  400: '#4d5969',
  300: '#344050',
  200: '#232e3c',
  100: '#0b1727',
  black: '#000'
};

export const getGrays = (isDark: boolean) => (isDark ? darkGrays : grays);

export const rgbColors = colors.map(color => rgbColor(color));
export const rgbaColors = colors.map(color => rgbaColor(color));

export const getColor = (name: string) => {
  const dom = document.documentElement;
  return getComputedStyle(dom).getPropertyValue(`--nodi-${name}`).trim();
};

//===============================
// Helpers
//===============================
export { getVisiblePageRange } from './getVisiblePageRange';

export const getPaginationArray = (totalSize: number, sizePerPage: number) => {
  const noOfPages = Math.ceil(totalSize / sizePerPage);
  const array = [];
  let pageNo = 1;
  while (pageNo <= noOfPages) {
    array.push(pageNo);
    pageNo = pageNo + 1;
  }
  return array;
};

export const capitalize = (str: string) =>
  (str.charAt(0).toUpperCase() + str.slice(1)).replace(/-/g, ' ');

export const camelize = (str: string) => {
  return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match: string, index: number) {
    if (+match === 0) return ''; // or if (/\s+/.test(match)) for white spaces
    return index === 0 ? match.toLowerCase() : match.toUpperCase();
  });
};
export const dashed = (str: string) => {
  return str.toLowerCase().replace(/ /g, '-');
};

//get chunk from array
export const chunk = (arr: any, chunkSize = 1, cache: any[] = []) => {
  const tmp = [...arr];
  if (chunkSize <= 0) return cache;
  while (tmp.length) cache.push(tmp.splice(0, chunkSize));
  return cache;
};

interface ContactPersonBase {
  Name?: string;
  PhoneNumberFixed?: string;
  PhoneNumberMobile?: string;
  Emailaddress?: string;
  Relationship?: string;
}

interface PersonManagementConfig<T extends ContactPersonBase> {
  getValues: UseFormGetValues<any>;
  setValue: UseFormSetValue<any>;
  currentCount: number;
  setCount: (value: number) => void;
  maxCount?: number;
  fieldArrayName: string;
}

export const handleRemovePerson = <T extends ContactPersonBase>({
  getValues,
  setValue,
  currentCount,
  setCount,
  fieldArrayName,
  index
}: PersonManagementConfig<T> & { index: number }) => {
  const currentValues = getValues();
  const updatedPersons = [...(currentValues[fieldArrayName] || [])];
  updatedPersons.splice(index, 1);
  setValue(fieldArrayName, updatedPersons);
  setCount(currentCount - 1);
};

export const handleAddPerson = <T extends ContactPersonBase>({
  currentCount,
  setCount,
  maxCount = 3
}: Pick<PersonManagementConfig<T>, 'currentCount' | 'setCount' | 'maxCount'>) => {
  if (currentCount < maxCount) {
    setCount(currentCount + 1);
  }
};

export const canRemovePerson = (currentCount: number): boolean => {
  return currentCount > 1;
};

export const canAddPerson = (currentCount: number, maxCount = 3): boolean => {
  return currentCount < maxCount;
};

export const getDutchInitials = (name: string): string => {
  // Convert to lowercase for easier comparison
  const lowerName = name.toLowerCase();
  
  // List of Dutch prefixes to exclude
  const dutchPrefixes = ['van', 'de', 'der', 'den', 'het', 'ter', "'t"];
  
  // Split the name into parts
  const nameParts = lowerName.split(' ');
  
  // Filter out Dutch prefixes and get initials
  const initials = nameParts
    .filter(part => !dutchPrefixes.includes(part))
    .map(part => part.charAt(0).toUpperCase());
  
  // Return first two initials if available
  return initials.slice(0, 2).join('');
};